import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faPlus,
  faCheck,
  faSpinner,
  faRotateRight,
  faSearch,
  faLayerGroup,
  faRulerHorizontal,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors.jsx';
import { useTranslation } from 'react-i18next';
import coilService from '../../services/coilService';
import { metresForKg, remainderAfterTake, formatKg, formatMetres } from '../../utils/coilMath';
import LooseCoilBadge from './LooseCoilBadge';

/**
 * CoilPickerModal
 *
 * Picks the specific coil rolls that will satisfy one order line — at order time, and
 * again at loading when the yard swaps a coil for the one actually on the floor.
 *
 * Props:
 *   isOpen        - boolean
 *   onClose       - fn
 *   warehouseId   - number, the line's warehouse (coil stock is warehouse-scoped)
 *   productSizeId - number, the line's product size
 *   targetKg      - number, the line quantity in kg (a target, never a hard limit)
 *   value         - array of { coil_id, take_kg, batch_number, colour, kg_per_m,
 *                             remaining_weight_kg, available_weight_kg }
 *   onChange      - fn(picks) called on Done
 *   context       - 'order' | 'loading', shown in the header so the operator knows
 *                   whether they are planning a pick or correcting one
 *   readOnly      - boolean, render the picks without letting them be edited
 *
 * The picks are held as a local draft while the modal is open and handed back on Done,
 * so an abandoned edit leaves the order line exactly as it was.
 */

// One warehouse × one product size is a small set; ask for all of it in one read so the
// colour chips and the batch search can work over the whole list client-side.
const PAGE_LIMIT = 500;

const SORTS = {
  OLDEST: 'oldest',
  NEWEST: 'newest',
  LIGHTEST: 'lightest',
  HEAVIEST: 'heaviest',
  BATCH: 'batch',
};

// FIFO by default — a yard ships the oldest coil first and it costs nothing to lead with it.
const DEFAULT_SORT = SORTS.OLDEST;

const BATCH_DEBOUNCE_MS = 300;

/** Whole kg everywhere — the database constraint, so the UI never offers a fraction. */
const toKg = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? Math.round(num) : 0;
};

/** Is this text a usable whole-kg weight, rather than blank or "12.5"? */
const isWholeKg = (text) => /^\d+$/.test(String(text ?? '').trim());

/** Grouped weight for the running totals: 4820 -> "4,820". */
const groupKg = (value) => toKg(value).toLocaleString('en-IN');

/** What the coil physically still carries — the figure a remainder is measured against. */
const remainingOf = (coil) => toKg(
  coil?.remaining_weight_kg ?? coil?.available_weight_kg ?? coil?.original_weight_kg
);

/** What this order may actually take: the remainder minus what other orders hold. */
const availableOf = (coil) => toKg(coil?.available_weight_kg ?? coil?.remaining_weight_kg);

const dateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Whole days since the coil was received, or null when the date is missing/unparsable. */
const ageInDays = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
};

const ageLabel = (days, t) => {
  if (days === null) return '';
  if (days === 0) return t('coils.picker.age.today');
  if (days < 60) return t('coils.picker.age.days', { days: String(days) });
  if (days < 730) return t('coils.picker.age.months', { months: String(Math.floor(days / 30)) });
  return t('coils.picker.age.years', { years: String(Math.floor(days / 365)) });
};

// Coil colours are free text typed by whoever received the stock ("Blue", "Sky Blue",
// "RAL 9002"). Anything the browser recognises as a CSS colour is drawn literally;
// everything else gets a hue derived from the name, so the same colour reads the same
// on every row instead of changing between renders.
const swatchCache = new Map();
const swatchFor = (colour) => {
  const name = String(colour || '').trim();
  if (!name) return UI_COLORS.BORDERS.LIGHT;
  if (swatchCache.has(name)) return swatchCache.get(name);

  const css = name.toLowerCase().replace(/\s+/g, '');
  let resolved;
  if (typeof window !== 'undefined' && window.CSS?.supports?.('color', css)) {
    resolved = css;
  } else {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 360;
    resolved = `hsl(${hash}, 55%, 62%)`;
  }

  swatchCache.set(name, resolved);
  return resolved;
};

const ColourDot = ({ colour, size = 12 }) => (
  <span
    className="inline-block rounded-full shrink-0"
    style={{
      width: size,
      height: size,
      backgroundColor: swatchFor(colour),
      border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.25)}`,
    }}
  />
);

/**
 * A draft row for one picked coil.
 *
 * takeText / remainderText are the raw input strings — kept as text so the operator can
 * clear a field and retype it without the value snapping back to 0 mid-keystroke. The
 * committed numbers are derived from them.
 */
const pickFromCoil = (coil) => ({
  coil_id: coil.id,
  batch_number: coil.batch_number || '',
  colour: coil.colour || '',
  kg_per_m: coil.kg_per_m,
  is_loose_coil: coil.is_loose_coil ?? false,
  remaining_weight_kg: remainingOf(coil),
  available_weight_kg: availableOf(coil),
  // Defaults to the whole coil: taking all of it is much the commonest pick.
  takeText: String(availableOf(coil)),
  remainderText: String(Math.max(0, remainingOf(coil) - availableOf(coil))),
});

const pickFromValue = (pick) => {
  const take = toKg(pick.take_kg);
  const remaining = remainingOf(pick);
  return {
    coil_id: pick.coil_id,
    batch_number: pick.batch_number || '',
    colour: pick.colour || '',
    kg_per_m: pick.kg_per_m,
    remaining_weight_kg: remaining,
    available_weight_kg: availableOf(pick),
    takeText: String(take),
    remainderText: String(Math.max(0, remaining - take)),
  };
};

/** Strip the editing-only fields before handing the picks back to the caller. */
const toValue = (pick) => ({
  coil_id: pick.coil_id,
  take_kg: toKg(pick.takeText),
  // Only when the operator moved it off the computed default. At the scale you weigh
  // what is LEFT on the roll, so this — not the take — is the figure the yard actually
  // has, and the server lets it win at consumption. Sending it unconditionally would
  // stamp every pick as "measured" when nobody measured anything.
  ...(toKg(pick.remainderText) !== Math.max(0, remainingOf(pick) - toKg(pick.takeText))
    ? { remainder_kg: toKg(pick.remainderText) }
    : {}),
  batch_number: pick.batch_number,
  colour: pick.colour,
  kg_per_m: pick.kg_per_m,
  remaining_weight_kg: pick.remaining_weight_kg,
  available_weight_kg: pick.available_weight_kg,
});

/**
 * The only hard rules, and they are all per coil: a take is a whole number of kg, above
 * zero, and no more than this coil has available. Picked-total against the line target is
 * NOT validated — over-picking is legitimate, you bill what ships.
 */
const rowError = (pick, t) => {
  const text = String(pick.takeText ?? '').trim();
  if (text === '' || toKg(text) <= 0) return t('coils.errors.takeMustBePositive');
  if (!isWholeKg(text)) return t('coils.errors.wholeKgRequired');
  if (toKg(text) > availableOf(pick)) {
    return t('coils.errors.takeExceedsAvailable', { available: formatKg(availableOf(pick)) });
  }
  return '';
};

const CoilPickerModal = ({
  isOpen,
  onClose,
  warehouseId,
  productSizeId,
  targetKg = 0,
  value,
  onChange,
  context = 'order',
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const [coils, setCoils] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [picks, setPicks] = useState([]);
  const [colourFilter, setColourFilter] = useState([]);
  const [batchText, setBatchText] = useState('');
  const [batchQuery, setBatchQuery] = useState('');
  const [sortKey, setSortKey] = useState(DEFAULT_SORT);

  const requestRef = useRef(0);
  const batchDebounceRef = useRef(null);

  // Seed the draft from the caller when the modal opens, and only then: re-seeding on
  // every `value` change would wipe out what is being typed.
  useEffect(() => {
    if (!isOpen) return;
    setPicks(Array.isArray(value) ? value.map(pickFromValue) : []);
    setColourFilter([]);
    setBatchText('');
    setBatchQuery('');
    setSortKey(DEFAULT_SORT);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCoils = useCallback(async () => {
    if (!isOpen || !warehouseId || !productSizeId) return;

    const token = requestRef.current + 1;
    requestRef.current = token;
    setLoading(true);
    setError('');

    try {
      const res = await coilService.getCoils({
        warehouse_id: warehouseId,
        product_size_id: productSizeId,
        available_only: true,
        limit: PAGE_LIMIT,
      });

      // A warehouse switch while a read is in flight must not be overwritten by the
      // answer to the question we no longer care about.
      if (requestRef.current !== token) return;

      if (res.success) {
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        setCoils(items);
        setTotal(res.data?.total ?? items.length);
      } else {
        setCoils([]);
        setTotal(0);
        setError(res.error || t('common.alerts.fetchFailed'));
      }
    } catch (err) {
      if (requestRef.current === token) {
        setCoils([]);
        setTotal(0);
        setError(err?.message || t('common.alerts.fetchFailed'));
      }
    } finally {
      // Only the CURRENT request may clear the flag. The stale-token `return` above also
      // used to skip the reset; releasing it here unconditionally would instead clear the
      // newer request's spinner while it is still in flight.
      if (requestRef.current === token) setLoading(false);
    }
  }, [isOpen, warehouseId, productSizeId, t]);

  useEffect(() => { loadCoils(); }, [loadCoils]);

  // The batch box searches batch number OR supplier ref, which the API's single `batch`
  // param cannot express, so the match runs over the fetched set. Debounced anyway —
  // re-filtering and re-rendering a few hundred rows on every keystroke is wasted work.
  const handleBatchChange = (text) => {
    setBatchText(text);
    if (batchDebounceRef.current) clearTimeout(batchDebounceRef.current);
    batchDebounceRef.current = setTimeout(() => {
      setBatchQuery(text.trim().toLowerCase());
    }, BATCH_DEBOUNCE_MS);
  };

  useEffect(() => () => {
    if (batchDebounceRef.current) clearTimeout(batchDebounceRef.current);
  }, []);

  const colourOptions = useMemo(() => {
    const seen = [];
    coils.forEach((coil) => {
      const colour = (coil.colour || '').trim();
      if (colour && !seen.includes(colour)) seen.push(colour);
    });
    return seen.sort((a, b) => a.localeCompare(b));
  }, [coils]);

  const visibleCoils = useMemo(() => {
    const filtered = coils.filter((coil) => {
      if (colourFilter.length && !colourFilter.includes((coil.colour || '').trim())) return false;
      if (!batchQuery) return true;
      const batch = String(coil.batch_number || '').toLowerCase();
      const supplier = String(coil.supplier_batch_ref || '').toLowerCase();
      return batch.includes(batchQuery) || supplier.includes(batchQuery);
    });

    const byDate = (coil) => {
      const time = new Date(coil.received_date).getTime();
      return Number.isNaN(time) ? 0 : time;
    };

    return filtered.sort((a, b) => {
      switch (sortKey) {
        case SORTS.NEWEST: return byDate(b) - byDate(a);
        case SORTS.LIGHTEST: return availableOf(a) - availableOf(b);
        case SORTS.HEAVIEST: return availableOf(b) - availableOf(a);
        case SORTS.BATCH: return String(a.batch_number || '').localeCompare(String(b.batch_number || ''));
        default: return byDate(a) - byDate(b);
      }
    });
  }, [coils, colourFilter, batchQuery, sortKey]);

  const pickedIds = useMemo(() => new Set(picks.map((pick) => pick.coil_id)), [picks]);

  const pickedKg = useMemo(
    () => picks.reduce((sum, pick) => sum + Math.max(0, toKg(pick.takeText)), 0),
    [picks]
  );

  const pickedMetres = useMemo(
    () => picks.reduce((sum, pick) => sum + metresForKg(pick, toKg(pick.takeText)), 0),
    [picks]
  );

  const hasRowError = useMemo(() => picks.some((pick) => rowError(pick, t) !== ''), [picks, t]);

  const toggleColour = (colour) => {
    setColourFilter((prev) => (
      prev.includes(colour) ? prev.filter((item) => item !== colour) : [...prev, colour]
    ));
  };

  const addCoil = (coil) => {
    setPicks((prev) => (
      prev.some((pick) => pick.coil_id === coil.id) ? prev : [...prev, pickFromCoil(coil)]
    ));
  };

  const removeCoil = (coilId) => {
    setPicks((prev) => prev.filter((pick) => pick.coil_id !== coilId));
  };

  /** Edit the take directly; the remainder follows. */
  const setTake = (coilId, text) => {
    setPicks((prev) => prev.map((pick) => {
      if (pick.coil_id !== coilId) return pick;
      const remainder = isWholeKg(text)
        ? String(Math.max(0, pick.remaining_weight_kg - toKg(text)))
        : '';
      return { ...pick, takeText: text, remainderText: remainder };
    }));
  };

  /**
   * Edit the LEFTOVER; the take follows.
   *
   * This is the direction the yard actually works in: the operator weighs the coil still
   * sitting on the scale, not the length that left on the truck. Making them subtract it
   * themselves is how a picker ends up being routed around, so the leftover is typed
   * straight in and the take is derived from the coil's remaining weight — which is the
   * same figure as available_weight_kg unless another order is holding part of this coil.
   */
  const setRemainder = (coilId, text) => {
    setPicks((prev) => prev.map((pick) => {
      if (pick.coil_id !== coilId) return pick;
      const take = isWholeKg(text)
        ? String(Math.max(0, pick.remaining_weight_kg - toKg(text)))
        : '';
      return { ...pick, remainderText: text, takeText: take };
    }));
  };

  const takeAll = (coilId) => setTake(coilId, String(availableOf(
    picks.find((pick) => pick.coil_id === coilId)
  )));

  const handleApply = () => {
    if (onChange) onChange(picks.map(toValue));
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  const target = toKg(targetKg);
  const delta = pickedKg - target;
  // Amber under, green on target (within a kilo), BLUE over — never red. Over-picking is
  // a legitimate outcome, you bill what ships.
  const barColour = !target || Math.abs(delta) <= 1
    ? UI_COLORS.STATUS.SUCCESS
    : (delta < 0 ? UI_COLORS.STATUS.WARNING : UI_COLORS.STATUS.INFO);
  const barPercent = target > 0 ? Math.min(100, Math.round((pickedKg / target) * 100)) : (pickedKg > 0 ? 100 : 0);

  const barLabel = target > 0
    ? t('coils.picker.summary.bar', {
      count: String(picks.length),
      picked: groupKg(pickedKg),
      required: groupKg(target),
    })
    : t('coils.picker.summary.barNoTarget', {
      count: String(picks.length),
      picked: groupKg(pickedKg),
    });

  const deltaLabel = !target
    ? ''
    : (Math.abs(delta) <= 1
      ? t('coils.picker.summary.exact')
      : (delta < 0
        ? t('coils.picker.summary.short', { amount: groupKg(-delta) })
        : t('coils.picker.summary.over', { amount: groupKg(delta) })));

  const inputStyle = {
    backgroundColor: UI_COLORS.FORMS.INPUT.background,
    borderColor: UI_COLORS.FORMS.INPUT.border,
    color: UI_COLORS.FORMS.INPUT.text,
  };

  const paneHeaderStyle = {
    color: UI_COLORS.TEXT.MUTED,
    borderColor: UI_COLORS.BORDERS.LIGHT,
    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.BACKGROUND, 0.35),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {context === 'loading' ? t('coils.picker.context.loading') : t('coils.picker.context.order')}
            </p>
            <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {t('coils.picker.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BORDERS.LIGHT, 0.5) }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body: available coils on the left, the picks on the right */}
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2">
          {/* LEFT — available coils */}
          <div className="flex flex-col min-h-0 border-b md:border-b-0 md:border-r" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <div className="sticky top-0 z-10 px-4 py-3 border-b shrink-0 flex flex-col gap-2" style={paneHeaderStyle}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide">
                  {t('coils.picker.panes.available')}
                </span>
                <button
                  type="button"
                  onClick={loadCoils}
                  className="text-xs px-2 py-1 rounded-lg transition-colors hover:bg-white"
                  style={{ color: UI_COLORS.TEXT.SECONDARY }}
                  title={t('common.buttons.refresh')}
                >
                  <FontAwesomeIcon icon={faRotateRight} spin={loading} />
                </button>
              </div>

              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs"
                  style={{ color: UI_COLORS.TEXT.MUTED }}
                />
                <input
                  type="text"
                  value={batchText}
                  onChange={(e) => handleBatchChange(e.target.value)}
                  placeholder={t('coils.picker.filters.searchPlaceholder')}
                  className="w-full pl-8 pr-3 py-2 rounded-xl border text-sm outline-none"
                  style={inputStyle}
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {colourOptions.map((colour) => {
                  const active = colourFilter.includes(colour);
                  return (
                    <button
                      key={colour}
                      type="button"
                      onClick={() => toggleColour(colour)}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border transition-colors"
                      style={{
                        backgroundColor: active ? colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.15) : UI_COLORS.BACKGROUNDS.CARD,
                        borderColor: active ? UI_COLORS.BRAND.TERTIARY : UI_COLORS.BORDERS.LIGHT,
                        color: UI_COLORS.TEXT.PRIMARY,
                      }}
                    >
                      <ColourDot colour={colour} size={10} />
                      {colour}
                    </button>
                  );
                })}
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  className="ml-auto px-2 py-1 rounded-lg border text-xs outline-none"
                  style={inputStyle}
                  aria-label={t('coils.picker.sort.label')}
                >
                  <option value={SORTS.OLDEST}>{t('coils.picker.sort.oldestFirst')}</option>
                  <option value={SORTS.NEWEST}>{t('coils.picker.sort.newestFirst')}</option>
                  <option value={SORTS.LIGHTEST}>{t('coils.picker.sort.lightestFirst')}</option>
                  <option value={SORTS.HEAVIEST}>{t('coils.picker.sort.heaviestFirst')}</option>
                  <option value={SORTS.BATCH}>{t('coils.picker.sort.batch')}</option>
                </select>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" style={{ color: UI_COLORS.TEXT.MUTED }} />
                </div>
              )}

              {!loading && error && (
                <div
                  className="rounded-xl px-4 py-3 text-sm"
                  style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.3)' }}
                >
                  {error}
                </div>
              )}

              {!loading && !error && visibleCoils.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
                  <FontAwesomeIcon icon={faLayerGroup} className="text-3xl" style={{ color: UI_COLORS.TEXT.MUTED }} />
                  <p className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {t('coils.picker.empty.title')}
                  </p>
                  <p className="text-xs max-w-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    {coils.length === 0 ? t('coils.picker.empty.noStock') : t('coils.picker.empty.subtitle')}
                  </p>
                </div>
              )}

              {!loading && !error && visibleCoils.map((coil) => {
                const picked = pickedIds.has(coil.id);
                const available = availableOf(coil);
                const held = Math.max(0, remainingOf(coil) - available);
                const days = ageInDays(coil.received_date);

                return (
                  <div
                    key={coil.id}
                    className="rounded-xl border px-3 py-2.5 transition-colors"
                    style={{
                      borderColor: picked ? UI_COLORS.BRAND.TERTIARY : UI_COLORS.BORDERS.LIGHT,
                      backgroundColor: picked ? colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.07) : UI_COLORS.BACKGROUNDS.CARD,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {/* Monospace: this is read straight off the tag stapled to the coil. */}
                        <div className="flex items-center gap-2">
                          <p className="font-mono font-bold text-sm truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {coil.batch_number}
                          </p>
                          <LooseCoilBadge coil={coil} />
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <ColourDot colour={coil.colour} />
                          <span className="text-xs truncate" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            {coil.colour || t('common.symbols.emDash')}
                          </span>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold leading-none" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                          {formatKg(available)} <span className="text-xs font-medium">{t('common.units.kg')}</span>
                        </p>
                        <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          {formatMetres(metresForKg(coil, available))} {t('common.units.m')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-end justify-between gap-3 mt-2">
                      <div className="min-w-0 text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        <p className="truncate">
                          {coil.supplier_batch_ref || t('common.symbols.emDash')}
                        </p>
                        <p className="flex items-center gap-1.5 mt-0.5">
                          {dateLabel(coil.received_date)}
                          {days !== null && (
                            <span
                              className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                              style={{
                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.SECONDARY, 0.1),
                                color: UI_COLORS.TEXT.SECONDARY,
                              }}
                            >
                              {ageLabel(days, t)}
                            </span>
                          )}
                        </p>
                        {held > 0 && (
                          <p className="mt-0.5" style={{ color: UI_COLORS.STATUS.WARNING }}>
                            {t('coils.picker.held', { weight: formatKg(held) })}
                          </p>
                        )}
                      </div>

                      {!readOnly && (
                        picked ? (
                          <span
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold shrink-0"
                            style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.12) }}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                            {t('coils.picker.added')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => addCoil(coil)}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold shrink-0 transition-all duration-200 hover:scale-105"
                            style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                            {t('coils.picker.add')}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                );
              })}

              {!loading && !error && total > coils.length && (
                <p className="text-xs text-center pt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  {t('coils.picker.truncated', { count: String(coils.length), total: String(total) })}
                </p>
              )}
            </div>
          </div>

          {/* RIGHT — picked coils */}
          <div className="flex flex-col min-h-0">
            <div className="sticky top-0 z-10 px-4 py-3 border-b shrink-0 flex items-center justify-between" style={paneHeaderStyle}>
              <span className="text-xs font-semibold uppercase tracking-wide">
                {t('coils.picker.panes.picked')}
              </span>
              {!readOnly && picks.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPicks([])}
                  className="text-xs font-medium px-2 py-1 rounded-lg transition-colors hover:bg-white"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {t('coils.picker.clearAll')}
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2">
              {picks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
                  <FontAwesomeIcon icon={faRulerHorizontal} className="text-3xl" style={{ color: UI_COLORS.TEXT.MUTED }} />
                  <p className="text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('coils.line.noneSelected')}</p>
                </div>
              )}

              {picks.map((pick) => {
                const available = availableOf(pick);
                const take = toKg(pick.takeText);
                const err = rowError(pick, t);
                const remainder = remainderAfterTake(pick, take);
                // "adjusted −4 kg": how far this row sits from the default, which is the
                // whole coil. Silent otherwise — most picks take everything.
                const adjustment = take - available;

                return (
                  <div
                    key={pick.coil_id}
                    className="rounded-xl border px-3 py-2.5"
                    style={{
                      borderColor: err ? UI_COLORS.BORDERS.ERROR : UI_COLORS.BORDERS.LIGHT,
                      backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono font-bold text-sm truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                          {pick.batch_number}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <ColourDot colour={pick.colour} />
                          <span className="text-xs truncate" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            {pick.colour || t('common.symbols.emDash')}
                          </span>
                          <span className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            · {formatKg(available)} {t('common.units.kg')}
                          </span>
                        </div>
                      </div>

                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => removeCoil(pick.coil_id)}
                          className="p-1.5 rounded-lg shrink-0 transition-colors"
                          style={{ color: UI_COLORS.TEXT.MUTED }}
                          title={t('coils.picker.remove')}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-2.5">
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <label className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {t('coils.picker.columns.take')}
                          </label>
                          {!readOnly && take !== available && (
                            <button
                              type="button"
                              onClick={() => takeAll(pick.coil_id)}
                              className="text-[11px] font-medium underline"
                              style={{ color: UI_COLORS.BRAND.TERTIARY }}
                            >
                              {t('coils.picker.takeAll')}
                            </button>
                          )}
                        </div>
                        {readOnly ? (
                          <p className="text-sm font-bold mt-1" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {formatKg(take)} {t('common.units.kg')}
                          </p>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            value={pick.takeText}
                            onChange={(e) => setTake(pick.coil_id, e.target.value)}
                            className="w-full mt-1 px-2 py-1.5 rounded-lg border text-sm font-semibold outline-none"
                            style={inputStyle}
                          />
                        )}
                        <p className="text-[11px] mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          {formatMetres(metresForKg(pick, take))} {t('common.units.m')}
                        </p>
                      </div>

                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.FORMS.LABEL }}>
                          {t('coils.picker.overrideRemainder')}
                        </label>
                        {readOnly ? (
                          <p className="text-sm font-bold mt-1" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {formatKg(remainder.weightKg)} {t('common.units.kg')}
                          </p>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            value={pick.remainderText}
                            onChange={(e) => setRemainder(pick.coil_id, e.target.value)}
                            className="w-full mt-1 px-2 py-1.5 rounded-lg border text-sm outline-none"
                            style={inputStyle}
                          />
                        )}
                        <p className="text-[11px] mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          {t('coils.picker.remainderLabel', {
                            weight: formatKg(remainder.weightKg),
                            length: formatMetres(remainder.lengthM),
                          })}
                        </p>
                      </div>
                    </div>

                    {adjustment !== 0 && !err && (
                      <span
                        className="inline-block mt-2 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{
                          backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.12),
                          color: UI_COLORS.STATUS.INFO,
                        }}
                      >
                        {t('coils.picker.adjusted', {
                          delta: `${adjustment > 0 ? '+' : '−'}${formatKg(Math.abs(adjustment))}`,
                        })}
                      </span>
                    )}

                    {err && (
                      <p className="text-[11px] mt-2 font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>
                        {err}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer — the running total against the line, never a blocker */}
        <div className="px-6 py-3 border-t shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div className="h-1.5 rounded-full overflow-hidden mb-2" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.15) }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${barPercent}%`, backgroundColor: barColour }}
            />
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm font-semibold flex items-center gap-1.5 flex-wrap" style={{ color: barColour }}>
              <span>{barLabel}</span>
              {deltaLabel && (
                <>
                  <span style={{ color: UI_COLORS.TEXT.MUTED }}>·</span>
                  <span>{deltaLabel}</span>
                </>
              )}
              <span style={{ color: UI_COLORS.TEXT.MUTED }}>·</span>
              <span className="font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {formatMetres(pickedMetres)} {t('common.units.m')}
              </span>
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BORDERS.LIGHT, 0.6) }}
              >
                {readOnly ? t('common.buttons.close') : t('common.buttons.cancel')}
              </button>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={hasRowError}
                  className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
                >
                  {t('common.buttons.done')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoilPickerModal;
